import { Search, MapPin } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { communes } from '@/mocks/communes';
import { useFadeUp } from '@/hooks/useFadeUp';

export function CommuneFinder() {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const ref = useFadeUp();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = communes.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div ref={ref} className="fade-up max-w-2xl mx-auto px-4 md:px-6">
        <div className="card border border-background-200 p-8 md:p-10 text-center">
          <div className="w-14 h-14 rounded-2xl bg-accent-50 flex items-center justify-center mx-auto mb-5">
            <Search className="w-7 h-7 text-accent-500" />
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-700 mb-3">
            Trouvez votre commune
          </h2>
          <p className="text-sm md:text-base text-foreground-500 mb-6">
            Sélectionnez votre commune pour accéder à nos services de réparation de volets roulants près de chez vous
          </p>

          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-400" />
              <input
                type="text"
                placeholder="Recherchez votre commune..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                className="w-full pl-12 pr-4 py-3.5 text-sm border border-background-300 rounded-xl focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-all text-base"
              />
            </div>

            {isOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-background-200 max-h-60 overflow-y-auto z-20">
                {filtered.map((commune) => (
                  <Link
                    key={commune.slug}
                    to={commune.urlPath}
                    onClick={() => {
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-500 transition-colors text-left w-full"
                  >
                    <MapPin className="w-4 h-4 shrink-0" />
                    {commune.name}
                  </Link>
                ))}
                {filtered.length === 0 && (
                  <p className="px-4 py-3 text-sm text-foreground-400 text-center">
                    Aucune commune trouvée
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}